import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Calendar, Clock, Upload, X, Image, Music, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_NUMBER = "919793498889";

const QuoteEstimatorPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    tattoo_size: '',
    placement: '',
    style: '',
    description: '',
    custom_design: false,
    reference_images: [],
    reference_urls: [],
    reference_previews: [], // For displaying previews
    music_preference: '',
    story: '',
    appointment_date: '',
    appointment_date_raw: '',
    appointment_time: '',
    preferred_artist: '',
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedSlots, setBookedSlots] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [uploadingRefs, setUploadingRefs] = useState(false);
  const [artistOptions, setArtistOptions] = useState([]);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const res = await axios.get(`${API}/artists`);
        if (Array.isArray(res.data)) {
          setArtistOptions(res.data.map((a) => a.name).filter(Boolean));
        }
      } catch (err) {
        console.error('Error loading artists:', err);
      }
    };
    fetchArtists();
  }, []);

  const sizeOptions = [
    { value: 'small', label: 'Small (< 3 inches)' },
    { value: 'medium', label: 'Medium (3-6 inches)' },
    { value: 'large', label: 'Large (6-10 inches)' },
    { value: 'extra-large', label: 'Extra Large (> 10 inches)' },
  ];

  const placementOptions = [
    'Arm', 'Forearm', 'Wrist', 'Hand', 'Chest', 'Back', 'Shoulder', 
    'Leg', 'Thigh', 'Ankle', 'Foot', 'Neck', 'Ribs'
  ];

  const styleOptions = [
    'Traditional', 'Neo-Traditional', 'Realism', 'Black & Grey',
    'Watercolor', 'Geometric', 'Minimalist', 'Japanese',
    'Tribal', 'Dotwork', 'Fine Line'
  ];

  const musicOptions = [
    'No preference',
    'Rock / Metal',
    'Hip Hop / R&B',
    'Pop',
    'EDM / Electronic',
    'Jazz / Blues',
    'Classical',
    'Indie / Alternative',
    'Podcasts / Audiobooks',
    'Silence please'
  ];

  const timeSlots = [
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM', '6:00 PM'
  ];

  // Fetch booked slots
  const fetchBookedSlots = useCallback(async () => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/appointments/availability`);
      setBookedSlots(response.data.booked_slots || {});
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (step === 4) {
      fetchBookedSlots();
    }
  }, [step, currentMonth, fetchBookedSlots]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle reference image upload with preview
  const handleReferenceImageChange = (e) => {
    const files = Array.from(e.target.files).filter(f => 
      f.size <= 10 * 1024 * 1024 && f.type.startsWith('image/')
    ).slice(0, 5);
    
    // Create preview URLs
    const previews = files.map(file => URL.createObjectURL(file));
    
    setFormData(prev => ({
      ...prev,
      reference_images: [...prev.reference_images, ...files].slice(0, 5),
      reference_previews: [...prev.reference_previews, ...previews].slice(0, 5)
    }));
  };

  const removeReferenceImage = (index) => {
    setFormData(prev => {
      // Revoke the object URL to free memory
      if (prev.reference_previews[index]) {
        URL.revokeObjectURL(prev.reference_previews[index]);
      }
      return {
        ...prev,
        reference_images: prev.reference_images.filter((_, i) => i !== index),
        reference_previews: prev.reference_previews.filter((_, i) => i !== index)
      };
    });
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = useMemo(() => getDaysInMonth(currentMonth), [currentMonth]);

  const isDateDisabled = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || date.getDay() === 0;
  };

  const formatDateForDisplay = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatDateForAPI = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toISOString().split('T')[0];
  };

  const isTimeSlotBooked = (time) => {
    if (!formData.appointment_date_raw) return false;
    const slots = bookedSlots[formData.appointment_date_raw] || [];
    return slots.some(slot => slot.time === time);
  };

  const getAvailableSlotsCount = (day) => {
    const dateKey = formatDateForAPI(day);
    const booked = bookedSlots[dateKey] || [];
    return timeSlots.length - booked.length;
  };

  const handleDateSelect = (day) => {
    if (!isDateDisabled(day)) {
      setSelectedDate(day);
      const formattedDate = formatDateForDisplay(day);
      const rawDate = formatDateForAPI(day);
      setFormData(prev => ({
        ...prev,
        appointment_date: formattedDate,
        appointment_date_raw: rawDate,
        appointment_time: ''
      }));
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
    setSelectedDate(null);
  };

  // Upload reference images and get shareable URLs
  const uploadReferenceImages = async () => {
    if (formData.reference_images.length === 0) return [];
    
    setUploadingRefs(true);
    const formDataUpload = new FormData();
    formData.reference_images.forEach(file => {
      formDataUpload.append('files', file);
    });

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedFiles = response.data.files || [];
      const urls = uploadedFiles.map(f => `${BACKEND_URL}${f.file_url}`);
      
      // Store URLs in form data
      setFormData(prev => ({ ...prev, reference_urls: urls }));
      return urls;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload reference images');
      return [];
    } finally {
      setUploadingRefs(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Upload reference images first
      let referenceUrls = [];
      if (formData.reference_images.length > 0) {
        referenceUrls = await uploadReferenceImages();
      }
      
      // Save quote request to backend
      await axios.post(`${API}/quote-requests`, {
        ...formData,
        reference_urls: referenceUrls
      });
      
      // Book the appointment (this locks the slot)
      await axios.post(`${API}/appointments`, {
        date: formData.appointment_date_raw,
        time: formData.appointment_time,
        client_name: formData.name,
        client_email: formData.email
      });
      
      // Build comprehensive WhatsApp message
      const sizeLabel = sizeOptions.find(s => s.value === formData.tattoo_size)?.label || formData.tattoo_size;
      
      let referenceLinksText = '';
      if (referenceUrls.length > 0) {
        referenceLinksText = `

📸 *REFERENCE IMAGES*
${referenceUrls.map((url, i) => `${i + 1}. ${url}`).join('\n')}`;
      }
      
      const message = `✨ *NEW TATTOO INQUIRY - Vision Tattoo* ✨

👤 *CUSTOMER DETAILS*
• Name: ${formData.name}
• Phone: ${formData.phone}
• Email: ${formData.email}

🎨 *TATTOO DETAILS*
• Size: ${sizeLabel}
• Placement: ${formData.placement}
• Style: ${formData.style}
${formData.description ? `• Description: ${formData.description}` : ''}

💭 *TATTOO STORY*
${formData.story || 'Not provided'}

📅 *APPOINTMENT*
• Date: ${formData.appointment_date}
• Time: ${formData.appointment_time}

👩‍🎨 *PREFERRED ARTIST*
${formData.preferred_artist || 'No Preference'}

🎵 *MUSIC PREFERENCE*
${formData.music_preference || 'No preference'}${referenceLinksText}

Looking forward to creating something beautiful together! 🖤`;

      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
      
      toast.success('Appointment booked! Opening WhatsApp...');
      
      // Try multiple methods to open WhatsApp
      setIsSubmitting(false);
      
      // Method 1: Try window.open first
      const newWindow = window.open(whatsappUrl, '_blank');
      
      // Method 2: If popup blocked, use location.href after a small delay
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Popup was blocked, try direct navigation
        setTimeout(() => {
          window.location.href = whatsappUrl;
        }, 100);
      }
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      if (error.response?.data?.detail === 'This time slot is already booked') {
        toast.error('This time slot was just booked by someone else. Please select another time.');
        fetchBookedSlots();
        setStep(4);
      } else {
        toast.error('Failed to submit. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  // Validation for each step
  const canProceedToStep = (targetStep) => {
    switch (targetStep) {
      case 2:
        return formData.tattoo_size && formData.placement && formData.style;
      case 3:
        return true; // Step 2 fields are optional
      case 4:
        return formData.name && formData.email && formData.phone;
      case 5:
        return formData.appointment_date && formData.appointment_time;
      default:
        return true;
    }
  };

  return (
    <div data-testid="quote-estimator-page" className="tattoo-bg-functional min-h-screen">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Header */}
          <div className="mb-8 md:mb-12 text-center">
            <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl font-black mb-3 md:mb-4 tracking-tight">GET YOUR QUOTE</h1>
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-gray-600 font-semibold">STEP {step} OF 5</p>
            {/* Progress bar */}
            <div className="flex gap-1 sm:gap-2 mt-3 md:mt-4 max-w-md mx-auto">
              {[1, 2, 3, 4, 5].map((s) => (
                <div
                  key={s}
                  className={`h-2 flex-1 transition-colors rounded-full ${s <= step ? 'bg-black' : 'bg-gray-300'}`}
                />
              ))}
            </div>
            {/* Step Labels */}
            <div className="hidden md:flex justify-between mt-2 max-w-md mx-auto text-[10px] font-mono uppercase tracking-wider text-gray-500">
              <span>Details</span>
              <span>Build</span>
              <span>Info</span>
              <span>Book</span>
              <span>Review</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 1: Tattoo Details */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="paper-texture p-6 md:p-12 space-y-8"
              >
                <h2 className="font-heading text-2xl md:text-4xl font-black mb-4 md:mb-6 tracking-tight">TATTOO DETAILS</h2>
                
                {/* Size Selection */}
                <div>
                  <label className="font-heading text-xl mb-4 block">Select Size *</label>
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    {sizeOptions.map((size) => (
                      <button
                        key={size.value}
                        type="button"
                        data-testid={`size-${size.value}`}
                        onClick={() => handleInputChange('tattoo_size', size.value)}
                        className={`p-4 border-2 text-left transition-all ${
                          formData.tattoo_size === size.value
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 hover:border-black'
                        }`}
                      >
                        <span className="font-heading text-sm md:text-base">{size.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Placement */}
                <div>
                  <label className="font-heading text-xl mb-4 block">Placement *</label>
                  <div className="flex flex-wrap gap-2 md:gap-3">
                    {placementOptions.map((placement) => (
                      <button
                        key={placement}
                        type="button"
                        data-testid={`placement-${placement.toLowerCase()}`}
                        onClick={() => handleInputChange('placement', placement)}
                        className={`px-4 py-2 border-2 font-mono text-xs uppercase tracking-wider transition-all ${
                          formData.placement === placement
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 hover:border-black'
                        }`}
                      >
                        {placement}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Style */}
                <div>
                  <label className="font-heading text-xl mb-4 block">Style *</label>
                  <div className="flex flex-wrap gap-2 md:gap-3">
                    {styleOptions.map((style) => (
                      <button
                        key={style}
                        type="button"
                        data-testid={`style-${style.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => handleInputChange('style', style)}
                        className={`px-4 py-2 border-2 font-mono text-xs uppercase tracking-wider transition-all ${
                          formData.style === style
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 hover:border-black'
                        }`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-6 flex justify-end">
                  <button
                    type="button"
                    data-testid="step1-next-button"
                    onClick={() => {
                      if (canProceedToStep(2)) {
                        setStep(2);
                      } else {
                        toast.error('Please select size, placement, and style');
                      }
                    }}
                    className="bg-black text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all flex items-center gap-2"
                  >
                    BUILD MY TATTOO <ChevronRight size={18} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Build My Tattoo */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="paper-texture p-6 md:p-12 space-y-8"
              >
                <h2 className="font-heading text-2xl md:text-4xl font-black mb-6 tracking-tight">BUILD MY TATTOO</h2>

                {/* Description */}
                <div>
                  <label className="font-heading text-xl mb-4 block">Describe Your Tattoo Idea</label>
                  <textarea
                    data-testid="description-input"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none font-body text-base resize-none"
                    placeholder="Describe what you want your tattoo to look like..."
                  />
                </div>

                {/* Story */}
                <div>
                  <label className="font-heading text-xl mb-4 block">Your Tattoo Story</label>
                  <p className="font-body text-sm text-gray-600 mb-3">
                    Share why you want this tattoo - the meaning, memory, or motivation behind it.
                  </p>
                  <textarea
                    data-testid="story-input"
                    value={formData.story}
                    onChange={(e) => handleInputChange('story', e.target.value)}
                    rows={4}
                    className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none font-body text-base resize-none"
                    placeholder="Tell us why this tattoo is meaningful to you..."
                  />
                </div>

                {/* Reference Images */}
                <div>
                  <label className="font-heading text-xl mb-4 block flex items-center gap-2">
                    <Image size={20} /> Reference Images
                  </label>
                  <p className="font-body text-sm text-gray-600 mb-3">
                    Upload images that inspire your tattoo idea (max 5 images, 10MB each)
                  </p>
                  <div className="border-2 border-dashed border-gray-300 p-6 text-center hover:border-black transition-colors">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      data-testid="reference-images-input"
                      id="ref-images-upload"
                      className="hidden"
                      onChange={handleReferenceImageChange}
                    />
                    <label htmlFor="ref-images-upload" className="cursor-pointer">
                      <Upload size={32} className="mx-auto mb-2 text-gray-400" />
                      <p className="font-body text-sm text-gray-600">Click to upload reference images</p>
                      <p className="font-mono text-xs text-gray-500 mt-1">JPG, PNG, or WEBP up to 10MB each</p>
                    </label>
                  </div>
                  
                  {/* Image Previews */}
                  {formData.reference_previews.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
                      {formData.reference_previews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={preview} 
                            alt={`Reference ${index + 1}`} 
                            className="w-full h-24 object-cover border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeReferenceImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Music Preference */}
                <div>
                  <label className="font-heading text-xl mb-4 block flex items-center gap-2">
                    <Music size={20} /> Music Preference
                  </label>
                  <p className="font-body text-sm text-gray-600 mb-3">
                    What would you like to listen to during your session?
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {musicOptions.map((music) => (
                      <button
                        key={music}
                        type="button"
                        data-testid={`music-${music.toLowerCase().replace(/[\s\/]+/g, '-')}`}
                        onClick={() => handleInputChange('music_preference', music)}
                        className={`px-3 py-2 border-2 font-mono text-xs transition-all text-left ${
                          formData.music_preference === music
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 hover:border-black'
                        }`}
                      >
                        {music}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-6 flex gap-4">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="border-2 border-black text-black px-8 py-3 font-bold text-sm uppercase tracking-wider hover:bg-black hover:text-white transition-all"
                  >
                    BACK
                  </button>
                  <button
                    type="button"
                    data-testid="step2-next-button"
                    onClick={() => setStep(3)}
                    className="bg-black text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all flex items-center gap-2"
                  >
                    YOUR INFORMATION <ChevronRight size={18} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Your Information */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="paper-texture p-6 md:p-12 space-y-8"
              >
                <h2 className="font-heading text-2xl md:text-4xl font-black mb-6 tracking-tight">YOUR INFORMATION</h2>

                <div className="space-y-6">
                  <div>
                    <label className="font-heading text-lg mb-2 block">Full Name *</label>
                    <input
                      type="text"
                      data-testid="name-input"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none font-body text-base"
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div>
                    <label className="font-heading text-lg mb-2 block">Email *</label>
                    <input
                      type="email"
                      data-testid="email-input"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none font-body text-base"
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="font-heading text-lg mb-2 block">Phone Number *</label>
                    <input
                      type="tel"
                      data-testid="phone-input"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none font-body text-base"
                      placeholder="9876543210"
                      required
                    />
                  </div>
                </div>

                <div className="pt-6 flex gap-4">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="border-2 border-black text-black px-8 py-3 font-bold text-sm uppercase tracking-wider hover:bg-black hover:text-white transition-all"
                  >
                    BACK
                  </button>
                  <button
                    type="button"
                    data-testid="step3-next-button"
                    onClick={() => {
                      if (canProceedToStep(4)) {
                        setStep(4);
                      } else {
                        toast.error('Please fill in all required fields');
                      }
                    }}
                    className="bg-black text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all flex items-center gap-2"
                  >
                    BOOK APPOINTMENT <ChevronRight size={18} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Book Appointment */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="paper-texture p-6 md:p-12 space-y-8"
              >
                <h2 className="font-heading text-2xl md:text-4xl font-black mb-2 tracking-tight">BOOK YOUR APPOINTMENT</h2>
                <p className="font-body text-gray-600 mb-6">Select your preferred date and time</p>

                {/* Calendar */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      type="button"
                      onClick={() => navigateMonth(-1)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <h3 className="font-heading text-xl">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>
                    <button
                      type="button"
                      onClick={() => navigateMonth(1)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>

                  {/* Weekday headers */}
                  <div className="grid grid-cols-7 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="text-center font-mono text-xs text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: startingDay }).map((_, i) => (
                      <div key={`empty-${i}`} className="h-12" />
                    ))}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const disabled = isDateDisabled(day);
                      const selected = selectedDate === day;
                      const availableSlots = getAvailableSlotsCount(day);
                      const fullyBooked = availableSlots === 0;

                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleDateSelect(day)}
                          disabled={disabled || fullyBooked}
                          className={`h-12 md:h-14 flex flex-col items-center justify-center transition-all text-sm relative ${
                            disabled || fullyBooked
                              ? 'text-gray-300 cursor-not-allowed'
                              : selected
                              ? 'bg-black text-white'
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          <span className="font-bold">{day}</span>
                          {!disabled && !fullyBooked && (
                            <span className="text-[10px] text-gray-500">{availableSlots} slots</span>
                          )}
                          {fullyBooked && !disabled && (
                            <span className="text-[10px] text-red-400">Full</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time Slots */}
                {formData.appointment_date && (
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <Clock size={18} /> Available Times for {formData.appointment_date.split(',')[0]}
                    </h3>
                    {loadingSlots ? (
                      <p className="text-gray-500">Loading available slots...</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                        {timeSlots.map((time) => {
                          const booked = isTimeSlotBooked(time);
                          const selected = formData.appointment_time === time;
                          
                          return (
                            <button
                              key={time}
                              type="button"
                              onClick={() => !booked && handleInputChange('appointment_time', time)}
                              disabled={booked}
                              data-testid={`time-slot-${time.replace(/[\s:]+/g, '-').toLowerCase()}`}
                              className={`py-3 px-2 border-2 font-mono text-xs transition-all ${
                                booked
                                  ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed line-through'
                                  : selected
                                  ? 'border-black bg-black text-white'
                                  : 'border-gray-300 hover:border-black'
                              }`}
                            >
                              {time}
                              {booked && <span className="block text-[10px]">Booked</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Summary */}
                {formData.appointment_date && formData.appointment_time && (
                  <div className="bg-green-50 border-2 border-green-200 p-4 flex items-center gap-3">
                    <Check className="text-green-600" size={24} />
                    <div>
                      <p className="font-bold text-green-800">Appointment Selected</p>
                      <p className="text-green-700">{formData.appointment_date} at {formData.appointment_time}</p>
                    </div>
                  </div>
                )}

                {/* Preferred Artist (optional) */}
                <div className="mt-8" data-testid="preferred-artist-field">
                  <label className="font-heading text-sm uppercase tracking-widest mb-3 block">
                    Preferred Tattoo Artist
                    <span className="ml-2 font-mono text-[10px] normal-case tracking-normal text-gray-500">(optional)</span>
                  </label>
                  <select
                    value={formData.preferred_artist}
                    onChange={(e) => setFormData({ ...formData, preferred_artist: e.target.value })}
                    data-testid="preferred-artist-select"
                    className="w-full border-2 border-gray-300 px-4 py-3 font-body focus:border-black focus:outline-none bg-white"
                  >
                    <option value="">No Preference</option>
                    {artistOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <p className="font-mono text-xs text-gray-500 mt-2">
                    Choose the artist you&apos;d like to book with, or leave as &ldquo;No Preference&rdquo;.
                  </p>
                </div>

                <div className="pt-6 flex gap-4">
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="border-2 border-black text-black px-8 py-3 font-bold text-sm uppercase tracking-wider hover:bg-black hover:text-white transition-all"
                  >
                    BACK
                  </button>
                  <button
                    type="button"
                    data-testid="review-button"
                    onClick={() => {
                      if (canProceedToStep(5)) {
                        setStep(5);
                      } else {
                        toast.error('Please select a date and time');
                      }
                    }}
                    className="bg-black text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all flex items-center gap-2"
                  >
                    REVIEW <ChevronRight size={18} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 5: Review */}
            {step === 5 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="paper-texture p-6 md:p-12 space-y-8"
              >
                <h2 className="font-heading text-2xl md:text-4xl font-black mb-6 tracking-tight">REVIEW YOUR REQUEST</h2>
                
                <div className="space-y-6 border-2 border-gray-300 p-6 md:p-8">
                  {/* Personal Info */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-1">Name</p>
                      <p className="font-body text-lg font-semibold">{formData.name}</p>
                    </div>
                    <div>
                      <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-1">Contact</p>
                      <p className="font-body text-base">{formData.email}</p>
                      <p className="font-body text-base">{formData.phone}</p>
                    </div>
                  </div>

                  <hr className="border-gray-200" />

                  {/* Appointment */}
                  <div className="bg-black text-white p-4 -mx-2">
                    <p className="font-heading text-sm uppercase tracking-widest text-gray-400 mb-1">Appointment</p>
                    <p className="font-heading text-xl">{formData.appointment_date}</p>
                    <p className="font-heading text-2xl font-bold">{formData.appointment_time}</p>
                  </div>

                  {/* Preferred Artist */}
                  <div data-testid="review-preferred-artist">
                    <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-1">Preferred Artist</p>
                    <p className="font-body text-lg font-semibold">
                      {formData.preferred_artist || 'No Preference'}
                    </p>
                  </div>

                  {/* Tattoo Details */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-1">Size</p>
                      <p className="font-body text-base">{sizeOptions.find(s => s.value === formData.tattoo_size)?.label}</p>
                    </div>
                    <div>
                      <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-1">Placement</p>
                      <p className="font-body text-base">{formData.placement}</p>
                    </div>
                    <div>
                      <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-1">Style</p>
                      <p className="font-body text-base">{formData.style}</p>
                    </div>
                  </div>

                  {formData.description && (
                    <div>
                      <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-1">Description</p>
                      <p className="font-body text-base">{formData.description}</p>
                    </div>
                  )}

                  {formData.story && (
                    <div>
                      <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-1">Your Tattoo Story</p>
                      <p className="font-body text-base italic">"{formData.story}"</p>
                    </div>
                  )}

                  {/* Music Preference */}
                  {formData.music_preference && (
                    <div className="flex items-center gap-3">
                      <Music size={20} className="text-gray-500" />
                      <div>
                        <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-1">Music Preference</p>
                        <p className="font-body text-base">{formData.music_preference}</p>
                      </div>
                    </div>
                  )}

                  {/* Reference Images Preview */}
                  {formData.reference_previews.length > 0 && (
                    <div>
                      <p className="font-heading text-sm uppercase tracking-widest text-gray-600 mb-3 flex items-center gap-2">
                        <Image size={16} /> Reference Images ({formData.reference_previews.length})
                      </p>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                        {formData.reference_previews.map((preview, index) => (
                          <img 
                            key={index}
                            src={preview} 
                            alt={`Reference ${index + 1}`} 
                            className="w-full h-20 object-cover border-2 border-gray-200"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="button"
                    data-testid="back-to-calendar-button"
                    onClick={() => setStep(4)}
                    className="border-2 border-black text-black px-8 py-3 font-bold text-sm uppercase tracking-wider hover:bg-black hover:text-white transition-all"
                  >
                    BACK
                  </button>
                  <button
                    type="submit"
                    data-testid="submit-quote-button"
                    disabled={isSubmitting || uploadingRefs}
                    className="flex-1 bg-green-600 text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting || uploadingRefs ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        {uploadingRefs ? 'UPLOADING IMAGES...' : 'BOOKING...'}
                      </>
                    ) : (
                      <>
                        SUBMIT & CHAT ON WHATSAPP
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default QuoteEstimatorPage;
